import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { landingMotionInView } from "@/utils/landingMotion";

interface FaqItem {
  id: number;
  question: string;
  answer: string;
}

function FaqAccordionRow({
  faq,
  isOpen,
  onToggle,
}: {
  faq: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <motion.div
      layout
      {...landingMotionInView}
      className={
        isOpen
          ? "faq-gradient"
          : "rounded-[16px] md:rounded-[22px] border border-white/20 bg-[rgba(255,255,255,0.02)]"
      }
    >
      <div
        className={`relative overflow-hidden rounded-[21px] ${isOpen
            ? ""
            : "bg-transparent"
          }`}
      >
        {isOpen ? (
          <div
            className="pointer-events-none absolute -left-[20%] -top-1/2 h-[140%] w-[70%] bg-[radial-gradient(ellipse_at_70%_0%,rgba(214,226,66,0.28),transparent_65%)] blur-2xl"
            aria-hidden
          />
        ) : null}

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          className="relative z-10 flex w-full items-start justify-between gap-4 px-5 py-3 text-left sm:px-6 "
        >
          <span className="font-creato text-lg font-bold text-white  md:text-xl ">
            {faq.question}
          </span>
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full   text-white">
            <AnimatePresence mode="wait" initial={false}>
              {isOpen ? (
                <motion.span
                  key="minus"
                  initial={{ opacity: 0, scale: 0.85, rotate: -90 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.85, rotate: 90 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex"
                  aria-hidden
                >
                  <Minus size={"28"} className="h-6 w-6 stroke-[2.5]" />
                </motion.span>
              ) : (
                <motion.span
                  key="plus"
                  initial={{ opacity: 0, scale: 0.85, rotate: 90 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.85, rotate: -90 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex"
                  aria-hidden
                >
                  <Plus className="h-6 w-6 stroke-[2.5]" />
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        </button>

        {/* Keep answers in the DOM so FAQ schema matches crawlable page content */}
        <div
          className={`relative z-10 overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
            isOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div
            className="px-5 pr-8 md:pr-16 xl:!pr-32 pb-5 pt-0 font-creato text-sm leading-relaxed text-white sm:px-6 sm:pb-6 md:text-lg"
            dangerouslySetInnerHTML={{ __html: faq.answer }}
          />
        </div>
      </div>
    </motion.div>
  );
}

export default function FaqSection() {
  const [activeId, setActiveId] = useState<number | null>(1);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const faqs: FaqItem[] = [
    {
      id: 1,
      question: t("Do prop firms really pay out?"),
      answer: t(
        "Yes. Payouts are issued to traders who meet the required performance targets and fully comply with all trading rules. URFX operates on a structured, performance-based model. For complete trading rules, visit <a href='/trading-rules' class='text-gradient'>https://urfx.io/trading-rules</a>."
      ),
    },
    {
      id: 2,
      question: t("What is a funded prop firm account?"),
      answer: t(
        "A funded prop firm account gives traders access to capital provided by the firm, allowing them to trade without risking their own funds while earning a share of eligible profits according to the firm's payout structure."
      ),
    },
    {
      id: 3,
      question: t("How does Instant Funding work?"),
      answer: t(
        "Instant Funding provides immediate access to a funded trading account without completing a multi-phase evaluation, provided all trading rules and risk parameters are followed. Learn more at <a href='/trading-rules' class='text-gradient'>https://urfx.io/trading-rules</a>."
      ),
    },
    {
      id: 4,
      question: t("Is anything guaranteed?"),
      answer: t(
        "No. Nothing is guaranteed. Profitability, account progression, and payouts are entirely performance-based and depend on your trading results and compliance with all applicable trading rules."
      ),
    },
    {
      id: 5,
      question: t("What happens if I break a trading rule?"),
      answer: t(
        "Violating trading rules may result in account failure, suspension, or disqualification from receiving payouts. Traders are responsible for understanding and following all trading rules at all times. See the complete rules at <a href='/trading-rules' class='text-gradient'>https://urfx.io/trading-rules</a>."
      ),
    },
    {
      id: 6,
      question: t("How long does it take to receive a payout?"),
      answer: t(
        "Payout timelines vary depending on the account type and your trading performance. URFX is designed to process eligible payouts as quickly and consistently as possible once all payout conditions have been satisfied."
      ),
    },
    {
      id: 7,
      question: t("Can I use Expert Advisors (EAs)?"),
      answer: t(
        "Yes. Expert Advisors (EAs) are permitted provided they comply with URFX trading rules, risk parameters, and any applicable strategy restrictions. Learn more at <a href='/trading-rules' class='text-gradient'>https://urfx.io/trading-rules</a>."
      ),
    },
    {
      id: 8,
      question: t("Is there a time limit to pass evaluations?"),
      answer: t(
        "Some URFX programs offer flexible or no strict time limits, allowing traders to focus on consistent performance instead of rushing to meet a deadline."
      ),
    },
    {
      id: 9,
      question: t("Can I lose my own money?"),
      answer: t(
        "No. You trade in a simulated environment and do not risk your own trading capital. However, failing to follow trading rules or proper risk management can result in account failure or disqualification from payouts."
      ),
    },
    {
      id: 10,
      question: t("Are there any additional fees?"),
      answer: t(
        "Any optional add-ons or upgrades are clearly displayed before checkout. URFX does not charge hidden fees."
      ),
    },
  ];

  return (
    <section className="relative overflow-visible bg-black  text-white py-10 xl:py-16">
      <div className="relative z-10 mx-auto max-w-7xl px-4 xl:px-0">
        <div className="grid items-start gap-12 xl:grid-cols-10 lg:gap-14 xl:gap-20">
          {/* Left — title */}
          <motion.div
            className="max-w-xl lg:sticky lg:top-28 xl:col-span-4"
            {...landingMotionInView}
          >
            <h2 className="font-nexa text-center md:text-start text-[36px] font-normal leading-[1.08] tracking-tight sm:text-[44px] lg:text-[52px]">
              <span className="block text-white">{t("Frequently")}</span>
              <span className="mt-1 italic font-normal block bg-gradient-to-r from-[#23d8e6] via-[#9DDF78] to-[#D6E242] bg-clip-text text-transparent">
                {t("Asked Questions")}
              </span>
            </h2>
            <p className="mt-6 font-nexa text-center md:text-start text-base leading-relaxed text-white/50 md:text-xl">
              {t(
                "Got any questions? "
              )}
              <br />
              {t(
                "Let us know! Reach out and our team will get right back to you."
              )}

            </p>
          </motion.div>

          {/* Right — accordion */}
          <motion.div
            className="flex flex-col gap-3 sm:gap-4 xl:col-span-6"
            {...landingMotionInView}
          >
            {faqs.map((faq) => (
              <FaqAccordionRow
                key={faq.id}
                faq={faq}
                isOpen={activeId === faq.id}
                onToggle={() =>
                  setActiveId(activeId === faq.id ? null : faq.id)
                }
              />
            ))}

            {/* CTA */}
            <div className="mt-7 flex justify-center">
              <div className=" ">
                <button
                  type="button"
                  onClick={() => navigate("/faqs")}
                  className="btn-gradient-left w-fit text-center min-w-[160px] rounded-[10px] px-6 py-2.5 text-lg font-bold"

                >
                  {t("Got more questions?")}
                </button>
              </div>
            </div>
          </motion.div>
        </div>


      </div>
    </section>
  );
}
